import { createRoute, Link } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import { motion, AnimatePresence } from 'motion/react';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Store, 
  MapPin, 
  Users, 
  TrendingUp, 
  ShieldCheck, 
  Clock, 
  ChevronRight,
  ArrowRight,
  CheckCircle2,
  DollarSign,
  Globe,
  Check,
  X,
  MessageCircle,
  Search,
  LayoutDashboard
} from 'lucide-react';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sell',
  component: VendorMarketingPage,
});

function VendorMarketingPage() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: "/dashboard-1.png",
      title: "Track Sales & Analytics",
      fitContain: true
    },
    {
      image: "/dashboard-2.png",
      title: "Vendor Guide & Tutorial",
      fitContain: true
    },
    {
      image: "/dashboard-3.png",
      title: "Manage Inventory Easily",
      fitContain: true
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const features = [
    {
      title: "Local Excellence",
      description: "Connect with neighbors looking for unique, local products they can't find anywhere else.",
      icon: MapPin,
    },
    {
      title: "Fair Terms",
      description: "Keep more of what you earn with our transparent fee structure and vendor-focused policies.",
      icon: DollarSign,
    },
    {
      title: "Instant Reach",
      description: "Our platform puts your products in front of thousands of active shoppers from day one.",
      icon: Users,
    },
    {
      title: "Easy Management",
      description: "A simple dashboard to manage orders, inventory, and customer questions without the tech headache.",
      icon: Store,
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Create your shop",
      description: "Tell us about your brand and what makes your products special."
    },
    {
      number: "02",
      title: "List your products",
      description: "Upload photos and details. Our platform optimizes them for search."
    },
    {
      number: "03",
      title: "Start selling",
      description: "Reach local customers and manage everything from one place."
    }
  ];

  return (
    <div className="bg-white dark:bg-neutral-950 min-h-screen selection:bg-neutral-900 selection:text-white">
      <Helmet>
        <title>Sell on Cardston Market - Start Your Local Online Store</title>
        <meta name="description" content="Set up your digital storefront on Cardston Market and connect with local buyers. Manage orders, inventory, and customer questions — all from one simple dashboard." />
        <link rel="canonical" href="https://www.cardstonmarket.com/sell" />
        <meta property="og:title" content="Sell on Cardston Market - Start Your Local Online Store" />
        <meta property="og:description" content="Set up your digital storefront and connect with local buyers. We handle the tech so you can focus on building your business." />
        <meta property="og:url" content="https://www.cardstonmarket.com/sell" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.cardstonmarket.com/og-banner.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.cardstonmarket.com/og-banner.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How to Start Selling on Cardston Market",
          "description": "Follow these simple steps to set up your digital storefront on Cardston Market and reach local customers.",
          "totalTime": "PT10M",
          "step": [
            {
              "@type": "HowToStep",
              "position": 1,
              "name": "Create your shop",
              "text": "Sign up for a Cardston Market account and tell us about your brand and what makes your products special.",
              "url": "https://www.cardstonmarket.com/login"
            },
            {
              "@type": "HowToStep",
              "position": 2,
              "name": "List your products",
              "text": "Upload photos and details. Our platform optimizes them for search so local customers can find you.",
              "url": "https://www.cardstonmarket.com/login"
            },
            {
              "@type": "HowToStep",
              "position": 3,
              "name": "Start selling",
              "text": "Reach local customers and manage everything from one place — orders, inventory, and customer questions.",
              "url": "https://www.cardstonmarket.com/login"
            }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How do I start selling on Cardston Market?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sign up for an account, apply to become a vendor, and once approved you can list products immediately from your vendor dashboard."
              }
            },
            {
              "@type": "Question",
              "name": "What does it cost to sell on Cardston Market?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Cardston Market charges a transparent 5% platform fee on each sale. There are no monthly fees or hidden charges."
              }
            },
            {
              "@type": "Question",
              "name": "How do I receive payments?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Connect your Stripe account through the vendor dashboard to receive payouts directly. We handle the payment processing securely."
              }
            },
            {
              "@type": "Question",
              "name": "How do customers pick up their orders?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You set your pickup schedule and location. Customers choose a pickup time during checkout and you get notified automatically."
              }
            }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Cardston Market Vendor Platform",
          "description": "A local marketplace platform that helps vendors sell handcrafted and unique products to their community with built-in order management, inventory tracking, and customer messaging.",
          "provider": {
            "@id": "https://www.cardstonmarket.com/#organization"
          },
          "areaServed": {
            "@type": "City",
            "name": "Cardston"
          },
          "serviceType": "Local Marketplace Platform"
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.cardstonmarket.com/" },
            { "@type": "ListItem", "position": 2, "name": "Sell", "item": "https://www.cardstonmarket.com/sell" }
          ]
        })}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-100 dark:bg-neutral-900 text-neutral-600 dark:text-neutral-400 text-xs font-medium mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                Now accepting new vendors
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold tracking-tight text-neutral-900 dark:text-white leading-[0.9] mb-6">
                Sell your products<br />
                <span className="text-neutral-500 italic">locally.</span>
              </h1>
              <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-lg mb-10 leading-relaxed">
                Set up your digital storefront and connect with local buyers. We handle the tech so you can focus on building your business.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Link
                  to="/login"
                  className="w-full sm:w-auto px-8 py-4 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-2xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                >
                  Start Selling Now
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
            
            <div className="mt-16 lg:mt-0 relative block">
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative z-10"
              >
                <div className="aspect-[5/4] lg:aspect-[6/5] w-full bg-neutral-100 dark:bg-neutral-900 rounded-[2rem] overflow-hidden shadow-2xl relative">
                  <AnimatePresence mode="wait">
                    <motion.img 
                      key={currentSlide}
                      src={slides[currentSlide].image} 
                      alt="Vendor dashboard preview"
                      className={`w-full h-full absolute inset-0 ${slides[currentSlide].fitContain ? 'object-contain p-4' : 'object-cover'}`}
                      referrerPolicy="no-referrer"
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    />
                  </AnimatePresence>
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 via-neutral-900/20 to-transparent" />
                  <div className="absolute bottom-8 left-8 right-8 z-20">
                    <AnimatePresence mode="wait">
                      <motion.div 
                        key={currentSlide}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.5 }}
                        className="flex items-center gap-3 mb-2"
                      >
                          <CheckCircle2 className="w-5 h-5 text-indigo-400" />
                          <span className="text-white font-medium text-lg">{slides[currentSlide].title}</span>
                      </motion.div>
                    </AnimatePresence>
                    
                    <div className="flex gap-2 mt-4">
                      {slides.map((_, idx) => (
                        <div 
                          key={idx}
                          className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/30'}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
              <div className="absolute -top-12 -right-12 w-64 h-64 bg-neutral-200 dark:bg-neutral-800 rounded-full blur-3xl opacity-50 z-0" />
              <div className="absolute -bottom-12 -left-12 w-64 h-64 bg-neutral-100 dark:bg-neutral-900 rounded-full blur-3xl opacity-50 z-0" />
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 bg-neutral-50 dark:bg-neutral-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl lg:text-5xl font-bold text-neutral-900 dark:text-white mb-4">Support for your business</h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              Basic tools to help you manage your store and connect with customers.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 bg-white dark:bg-neutral-900 rounded-[2rem] border border-neutral-100 dark:border-neutral-800 shadow-sm"
              >
                <div className="w-12 h-12 bg-neutral-900 dark:bg-white rounded-2xl flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-white dark:text-neutral-900" />
                </div>
                <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 gap-12 items-start">
            <div className="lg:sticky lg:top-24 mb-16 lg:mb-0">
              <h2 className="text-4xl font-bold text-neutral-900 dark:text-white mb-6 leading-tight">
                How to get started
              </h2>
              <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-8">
                Follow these simple steps to set up your digital storefront.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-neutral-900 dark:text-white font-bold group"
              >
                Create your account 
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            
            <div className="lg:col-span-2 space-y-12">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-8 group">
                  <div className="flex-shrink-0">
                    <div className="text-5xl font-black text-neutral-100 dark:text-neutral-900 group-hover:text-neutral-200 dark:group-hover:text-neutral-800 transition-colors duration-300">
                      {step.number}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">{step.title}</h3>
                    <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-lg leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 bg-neutral-50 dark:bg-neutral-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-neutral-900 dark:text-white mb-4">A better way to sell</h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              How we compare to traditional social media selling methods.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr>
                  <th className="p-6 text-sm font-bold uppercase tracking-widest text-neutral-400">Features</th>
                  <th className="p-6 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-t-3xl text-center font-bold text-xl">Cardston Market</th>
                  <th className="p-6 text-center font-semibold text-neutral-600 dark:text-neutral-400">FB Marketplace</th>
                  <th className="p-6 text-center font-semibold text-neutral-600 dark:text-neutral-400">FB Groups</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {[
                  { 
                    feature: "Local Community Focus", 
                    icon: Globe,
                    us: true, fbM: false, fbG: true,
                    desc: "Built specifically for our town, not a global algorithm."
                  },
                  { 
                    feature: "Easy Product Search", 
                    icon: Search,
                    us: true, fbM: true, fbG: false,
                    desc: "Customers find you via categories, not just scrolling feeds."
                  },
                  { 
                    feature: "Professional Dashboard", 
                    icon: LayoutDashboard,
                    us: true, fbM: false, fbG: false,
                    desc: "Track inventory, sales, and analytics in one place."
                  },
                  { 
                    feature: "Order Management", 
                    icon: CheckCircle2,
                    us: true, fbM: false, fbG: false,
                    desc: "Move past messy DMs to structured order tracking."
                  },
                  { 
                    feature: "No 'Is this available?' Spam", 
                    icon: MessageCircle,
                    us: true, fbM: false, fbG: false,
                    desc: "Structured checkout instead of endless automated DMs."
                  },
                  { 
                    feature: "Verified Vendor Status", 
                    icon: ShieldCheck,
                    us: true, fbM: false, fbG: true,
                    desc: "High trust with known local neighbors and businesses."
                  },
                  { 
                    feature: "No Feed Noise", 
                    icon: TrendingUp,
                    us: true, fbM: false, fbG: false,
                    desc: "Your listings don't get buried by memes or status updates."
                  }
                ].map((row, i) => (
                  <tr key={i} className="group hover:bg-neutral-100/50 dark:hover:bg-neutral-900/50 transition-colors">
                    <td className="p-6">
                      <div className="flex items-center gap-3">
                        <row.icon className="w-5 h-5 text-neutral-400" />
                        <div>
                          <p className="font-bold text-neutral-900 dark:text-white">{row.feature}</p>
                          <p className="text-xs text-neutral-500">{row.desc}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6 bg-neutral-900/5 dark:bg-white/5 text-center">
                      <div className="flex justify-center">
                        <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-500" />
                        </div>
                      </div>
                    </td>
                    <td className="p-6 text-center">
                      <div className="flex justify-center">
                        {row.fbM ? (
                          <Check className="w-5 h-5 text-neutral-400" />
                        ) : (
                          <X className="w-5 h-5 text-neutral-300 dark:text-neutral-700" />
                        )}
                      </div>
                    </td>
                    <td className="p-6 text-center">
                      <div className="flex justify-center">
                        {row.fbG ? (
                          <Check className="w-5 h-5 text-neutral-400" />
                        ) : (
                          <X className="w-5 h-5 text-neutral-300 dark:text-neutral-700" />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="p-6"></td>
                  <td className="p-6 bg-neutral-900 dark:bg-white rounded-b-3xl"></td>
                  <td className="p-6"></td>
                  <td className="p-6"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      {/* Testimonial / Social Proof */}
      <section className="py-24 bg-neutral-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="relative z-10">
            <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-12 max-w-3xl">
              "A simple way to manage our local deliveries and reach more people in town."
            </h2>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-neutral-800 overflow-hidden">
                  <img 
                     src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop" 
                     alt="The Artisan Bakery vendor testimonial on Cardston Market"
                     className="w-full h-full object-cover"
                     referrerPolicy="no-referrer"
                     loading="lazy"
                   />
              </div>
              <div>
                <div className="font-bold text-xl">The Artisan Bakery</div>
                <div className="text-neutral-400">(Fake/Demo) Vendor since 2026</div>
              </div>
            </div>
          </div>
          {/* Abstract elements */}
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-[600px] h-[600px] bg-neutral-800/20 rounded-full blur-3xl" />
        </div>
      </section>

      {/* CTA Footer Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-neutral-100 dark:bg-neutral-900 rounded-[3rem] p-12 lg:p-24 text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white mb-8">Ready to start?</h2>
            <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto mb-12">
              Sign up today and begin reaching out to local customers.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/login"
                className="w-full sm:w-auto px-10 py-5 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-2xl font-bold text-lg hover:opacity-90 transition-opacity"
              >
                Sign Up
              </Link>
            </div>
            <div className="mt-12 flex items-center justify-center gap-8 opacity-50 grayscale contrast-125">
               <Store className="w-8 h-8" />
               <Users className="w-8 h-8" />
               <MapPin className="w-8 h-8" />
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer Links */}
      <footer className="py-12 border-t border-neutral-100 dark:border-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-sm text-neutral-500 dark:text-neutral-400 uppercase tracking-widest font-bold">
            Vendor Portal
          </div>
          <div className="flex items-center gap-8">
            <Link to="/terms" className="text-sm text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="text-sm text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors">Privacy Policy</Link>
            <a href="https://mail.google.com/mail/?view=cm&fs=1&to=support@cardstonmarket.com" target="_blank" rel="noopener noreferrer" className="text-sm text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
